/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import type { RoleAssignments } from '@modules/cloud-api/v1/types'
import {
  filterActiveFlatRoleAssignments,
  flattenRoleAssignments,
  getAllRoleAssignments,
} from '@modules/role-assignments-lib'
import type { ResourceType, RoleLabel } from '@modules/role-assignments-lib/types'
import {
  CUSTOM_ROLE_BADGE_LABEL,
  RESOURCE_TYPES,
  resourceRoleLabelByRoleId,
} from '@modules/role-assignments-lib/types'

import { useGetNormalizedDeploymentAndProjectList } from '../../lib'

export type RoleBadgeInfo = {
  resourceType?: ResourceType
  roleLabel: RoleLabel
  count?: number | 'all'
}

/**
 * @property {RoleAssignments} roleAssignments: the role assignments to be processed
 * @property {boolean} customRolesEnabled: whether custom roles are enabled or not
 * @property {string | undefined} organizationId: this is required in AC, but optional in UC
 * @property {boolean} skipFilterResources: whether to skip filtering for only roles with active resources or not
 */
export type UseGetRoleBadgeInfoListParams = {
  roleAssignments: RoleAssignments
  customRolesEnabled: boolean
  organizationId: string | undefined
  skipFilterResources: boolean
}

/**
 * @returns a list of role badges with their labels and counts
 */
export function useGetRoleBadgeInfoList({
  roleAssignments,
  customRolesEnabled,
  organizationId,
  skipFilterResources,
}: UseGetRoleBadgeInfoListParams): RoleBadgeInfo[] {
  const userResourceList = useGetNormalizedDeploymentAndProjectList(organizationId)
  const flattenedRoleAssignments = flattenRoleAssignments(roleAssignments)

  const normalizedRoleAssignments = skipFilterResources
    ? flattenedRoleAssignments
    : filterActiveFlatRoleAssignments({
        flattenedRoleAssignments,
        userResourceList,
      })

  // Filter out multiple ALL assignments if custom roles are disabled
  // The conditional assignment below can be removed one custom roles are enabled and the feature flag is deleted
  // Replace with this assignment:
  // const allRoleAssignments = getAllRoleAssignments(normalizedRoleAssignments)
  const allRoleAssignments = customRolesEnabled
    ? getAllRoleAssignments(normalizedRoleAssignments)
    : getAllRoleAssignments(
        normalizedRoleAssignments.filter(
          (value, index, array) =>
            array.findIndex((a) => a.resourceType === value.resourceType) === index,
        ),
      )

  // Determine which ALL roles apply to all resources and handle those first
  const badges: RoleBadgeInfo[] = []
  const uniqueAllRoleLabels = [
    ...new Set(
      allRoleAssignments.map(
        (allRoleAssignment) => resourceRoleLabelByRoleId[allRoleAssignment.role_id],
      ),
    ),
  ]

  const globalAllRoleLabels = new Set<RoleLabel>()
  uniqueAllRoleLabels.forEach((roleLabel) => {
    const matchedAssignments = allRoleAssignments.filter(
      (assignment) => resourceRoleLabelByRoleId[assignment.role_id] === roleLabel,
    )

    if (matchedAssignments.length === RESOURCE_TYPES.length) {
      // Add to global all assignments
      globalAllRoleLabels.add(roleLabel)

      // Create global all badge
      badges.push({
        roleLabel,
        count: 'all',
      })
    }
  })

  // Filter out multiple assignments if custom roles are disabled
  // These constants can be removed one custom roles are enabled and the feature flag is deleted
  const filteredProjects: string[] = []
  const filteredAll: ResourceType[] = []

  // Handle the rest of the assignments
  normalizedRoleAssignments.forEach((roleAssignment) => {
    /*
     * FIXME this should be gone, CCM role assignments don't have the application_roles property, but the type now
     * bundles all role assignment types together: 2025/9/3
     */
    const application_roles =
      'application_roles' in roleAssignment && (roleAssignment.application_roles as string[])

    const { role_id, ids, resourceType } = roleAssignment
    const roleLabel = resourceRoleLabelByRoleId[role_id]

    // Skip any global all assignments that were handled above
    if (!(roleAssignment.all && globalAllRoleLabels.has(roleLabel))) {
      let foundBadge: RoleBadgeInfo | undefined
      let customRole = false

      if (
        customRolesEnabled &&
        application_roles &&
        application_roles.some((role) => role !== roleLabel)
      ) {
        customRole = true
        foundBadge = badges.find(
          (badge) =>
            badge.roleLabel === CUSTOM_ROLE_BADGE_LABEL && badge.resourceType === resourceType,
        )
      }

      if (customRole && foundBadge && typeof foundBadge.count === 'number') {
        foundBadge.count += 1 // we're counting number of affected projects, not number of roles assigned
      } else {
        // This IF block below filters out multiple role assignments when custom roles is disabled
        // Once the custom roles flag is deleted and the feature is officially enabled, the IF
        // can be removed and replaced with just the badges.push call.

        if (
          customRolesEnabled ||
          (ids
            ? ids.find((id) => !filteredProjects.includes(id))
            : resourceType && !filteredAll.includes(resourceType))
        ) {
          badges.push({
            resourceType,
            roleLabel: customRole ? CUSTOM_ROLE_BADGE_LABEL : roleLabel,
            count: ids?.length || 'all',
          })

          // if custom roles are disabled, build list to eliminate multiple assignments
          // The next block can be removed once custom roles are enabled and the feature flag is deleted

          if (!customRolesEnabled) {
            if (ids) {
              filteredProjects.push(...ids)
            } else {
              filteredAll.push(resourceType as ResourceType)
            }
          }
        }
      }
    }
  })

  return badges
}
